import { Checkbox, Flex, Tbody, Td, Th, Tr, Text } from "@chakra-ui/react";
import { Table, Thead } from "@chakra-ui/react";
import { TableContainer } from "@chakra-ui/react";
import { useState } from "react";
import { FeeModel } from "providers/loans/fee.model";
import { FeeTableActionsMenu } from "./fee-table-actions-menu";
import { DownloadButton } from "./download-button";
import { FeeAccount } from "./models/fee-account";
import { useCustomFormats } from "hooks/useCustomFormat";
import { useTenant } from "modules/tenant/providers/tenant-provider";

interface FeeTableProps {
  fees: FeeModel[];
  accounts: FeeAccount[];
  handleEdit: (fee: FeeModel) => void;
  handleDelete: (id: number) => void;
}

export function FeeTable({ fees, accounts, handleEdit, handleDelete }: FeeTableProps) {
  const { user } = useTenant();
  const { formatDate, formatCurrency } = useCustomFormats();

  const [selectedFees, setSelectedFees] = useState<FeeModel[]>([]);

  const showAccountsColumns = accounts.length > 0;

  function getAccountName(code: string | null) {
    if (!code) return "--";
    const account = accounts.find(a => a.code === code);
    return account?.description ?? "--";
  }

  function handleSelect(fee: FeeModel, isSelected: boolean) {
    if (isSelected) {
      setSelectedFees([...selectedFees, fee]);
    } else {
      setSelectedFees(selectedFees.filter(f => f.id !== fee.id));
    }
  }

  function handleSelectAll(isSelected: boolean) {
    if (isSelected) {
      setSelectedFees(fees);
    } else {
      setSelectedFees([]);
    }
  }

  return (
    <TableContainer>
      <Table>
        <Thead>
          <Tr>
            <Th aria-label="Select all" w="36px">
              <Flex justifyContent="center">
                <Checkbox
                  colorScheme="gray"
                  isChecked={fees.length > 0 && selectedFees.length === fees.length}
                  onChange={e => handleSelectAll(e.target.checked)}
                  isDisabled={fees.length === 0}
                />
              </Flex>
            </Th>
            <Th>Description</Th>
            <Th>Fee Date</Th>
            <Th>Fee Amount</Th>
            {showAccountsColumns && <Th>Account</Th>}
            {user.type === "lender" && (
              <Th textAlign="center">
                <Flex justifyContent="center">
                  <DownloadButton selectedFees={selectedFees} />
                </Flex>
              </Th>
            )}
          </Tr>
        </Thead>
        <Tbody>
          {fees.length === 0 && (
            <Tr>
              <Td colSpan={showAccountsColumns ? 6 : 5} textAlign="center">
                <Text p={4}>No fees yet.</Text>
              </Td>
            </Tr>
          )}
          {fees.map((fee: FeeModel) => (
            <Tr key={fee.id}>
              <Td>
                <Flex justifyContent="center">
                  <Checkbox
                    colorScheme="gray"
                    isChecked={selectedFees.includes(fee)}
                    onChange={e => handleSelect(fee, e.target.checked)}
                  />
                </Flex>
              </Td>
              <Td>{fee.description}</Td>
              <Td>{formatDate(fee.fee_date)}</Td>
              <Td>{formatCurrency(fee.fee_amount ?? 0)}</Td>
              {showAccountsColumns && <Td>{getAccountName(fee.account ?? null)}</Td>}
              {user.type === "lender" && (
                <Td w="36px">
                  <Flex justifyContent="center">
                    <FeeTableActionsMenu
                      fee={fee}
                      handleEdit={() => handleEdit(fee)}
                      handleDelete={handleDelete}
                    />
                  </Flex>
                </Td>
              )}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}
